import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Field, inputCls, Modal, Page, PageHeader, PrimaryButton, StatusBadge } from "@/components/lims";
import { CalendarBlank, CaretLeft, CaretRight, Clock, Gauge } from "@phosphor-icons/react";

const dateKey = (date) => date.toISOString().slice(0, 10);
const addDays = (value, amount) => {
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + amount);
  return dateKey(date);
};
const localInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};
const localDay = (value) => {
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return dateKey(local);
};

export default function Schedule() {
  const [start, setStart] = useState(() => dateKey(new Date()));
  const [board, setBoard] = useState({ tests: [], capacity: [], unscheduled_count: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ user_id: "", planned_start: "", planned_completion: "", estimated_hours: "" });
  const [saving, setSaving] = useState(false);
  const end = addDays(start, 6);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/schedule", { params: { date_from: start, date_to: addDays(start, 6) } });
      setBoard(data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || error.message);
    } finally {
      setLoading(false);
    }
  }, [start]);
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(start, index)), [start]);
  const scheduledByDay = useMemo(() => {
    const grouped = Object.fromEntries(days.map((day) => [day, []]));
    board.tests.filter((test) => test.planned_start).forEach((test) => {
      const day = localDay(test.planned_start);
      if (grouped[day]) grouped[day].push(test);
    });
    return grouped;
  }, [board.tests, days]);
  const unscheduled = board.tests.filter((test) => !test.planned_start);

  const edit = (test) => {
    setSelected(test);
    setForm({
      user_id: test.assigned_to_id || "",
      planned_start: localInput(test.planned_start),
      planned_completion: localInput(test.planned_completion),
      estimated_hours: test.estimated_hours || "",
    });
  };
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const selectedUser = form.user_id || null;
      if (selectedUser !== (selected.assigned_to_id || null)) {
        await api.post(`/tests/${selected.id}/assign`, { user_id: selectedUser });
      }
      await api.patch(`/tests/${selected.id}/schedule`, {
        planned_start: form.planned_start ? new Date(form.planned_start).toISOString() : null,
        planned_completion: form.planned_completion ? new Date(form.planned_completion).toISOString() : null,
        estimated_hours: form.estimated_hours ? Number(form.estimated_hours) : null,
      });
      toast.success(`${selected.test_code} schedule updated`);
      setSelected(null);
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <PageHeader title="Schedule & Capacity" subtitle="Plan analyst work, compare team load, and keep actual execution dates separate"
        action={<div className="flex items-center gap-2"><button aria-label="Previous week" onClick={() => setStart(addDays(start, -7))} className="rounded border border-slate-200 bg-white p-2"><CaretLeft size={18} /></button><div className="rounded border border-slate-200 bg-white px-3 py-2 text-sm">{start} – {end}</div><button aria-label="Next week" onClick={() => setStart(addDays(start, 7))} className="rounded border border-slate-200 bg-white p-2"><CaretRight size={18} /></button></div>} />

      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="capacity-cards">
        {board.capacity.map((analyst) => {
          const overloaded = analyst.utilization_percent > 100;
          return <div key={analyst.user_id} className="rounded-lg border border-slate-200 bg-white p-4"><div className="flex items-center justify-between"><div><div className="font-medium text-slate-900">{analyst.name}</div><div className="text-xs text-slate-400">{analyst.open} open tests</div></div><Gauge size={21} className={overloaded ? "text-status-critical" : "text-slate-400"} /></div><div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${overloaded ? "bg-status-critical" : "bg-status-info"}`} style={{ width: `${Math.min(analyst.utilization_percent, 100)}%` }} /></div><div className="mt-2 flex justify-between text-xs"><span>{analyst.planned_hours}h planned</span><span className={overloaded ? "font-medium text-status-critical" : "text-slate-400"}>{analyst.utilization_percent}% of {analyst.capacity_hours}h</span></div></div>;
        })}
      </div>

      <section className="mb-5 rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4"><Clock size={19} className="text-slate-400" /><div><h2 className="font-medium">Unscheduled work</h2><p className="text-xs text-slate-400">{board.unscheduled_count} open tests need a planned slot</p></div></div>
        <div className="flex gap-3 overflow-x-auto p-4">{loading ? <div className="h-20 w-full animate-pulse rounded bg-slate-100" /> : unscheduled.length === 0 ? <p className="py-4 text-sm text-slate-400">All open work in this view is scheduled.</p> : unscheduled.map((test) => <button key={test.id} onClick={() => edit(test)} className="min-w-[220px] rounded-lg border border-dashed border-slate-300 p-3 text-left hover:border-slate-500"><div className="font-mono text-[11px] text-slate-400">{test.test_code}</div><div className="mt-1 text-sm font-medium">{test.assay_name}</div><div className="mt-2 text-xs text-slate-500">{test.sample_code} · {test.assigned_to || "Unassigned"}</div></button>)}</div>
      </section>

      <section className="overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid="schedule-calendar">
        <div className="grid min-w-[1050px] grid-cols-7 divide-x divide-slate-100">
          {days.map((day) => <div key={day} className="min-h-[360px]"><div className="border-b border-slate-100 bg-slate-50 px-3 py-3"><div className="text-xs uppercase text-slate-400">{new Date(`${day}T12:00:00`).toLocaleDateString([], { weekday: "short" })}</div><div className="font-medium">{new Date(`${day}T12:00:00`).toLocaleDateString([], { month: "short", day: "numeric" })}</div></div><div className="space-y-2 p-2">{(scheduledByDay[day] || []).map((test) => <button key={test.id} onClick={() => edit(test)} className="w-full rounded-md border border-slate-200 bg-white p-3 text-left shadow-sm hover:border-slate-400"><div className="text-[11px] text-slate-400">{new Date(test.planned_start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {test.estimated_hours || "—"}h</div><div className="mt-1 text-sm font-medium">{test.assay_name}</div><div className="mt-1 font-mono text-[10px] text-slate-400">{test.test_code}</div><div className="mt-2"><StatusBadge status={test.status} /></div><div className="mt-2 truncate text-xs text-slate-500">{test.assigned_to || "Unassigned"}</div></button>)}</div></div>)}
        </div>
      </section>

      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected ? `Plan ${selected.test_code}` : "Plan test"} testid="schedule-modal">
        {selected && <form onSubmit={save} className="space-y-4"><div className="rounded bg-slate-50 p-3"><div className="font-medium">{selected.assay_name}</div><div className="text-xs text-slate-500">{selected.sample_code} · {selected.sample_type}</div></div><Field label="Analyst"><select value={form.user_id} onChange={(event) => setForm({ ...form, user_id: event.target.value })} className={inputCls}><option value="">Unassigned</option>{board.capacity.map((analyst) => <option key={analyst.user_id} value={analyst.user_id}>{analyst.name} · {analyst.planned_hours}/{analyst.capacity_hours}h</option>)}</select></Field><div className="grid grid-cols-2 gap-4"><Field label="Planned start"><input type="datetime-local" value={form.planned_start} onChange={(event) => setForm({ ...form, planned_start: event.target.value })} className={inputCls} /></Field><Field label="Planned completion"><input type="datetime-local" value={form.planned_completion} onChange={(event) => setForm({ ...form, planned_completion: event.target.value })} className={inputCls} /></Field></div><Field label="Estimated hours"><input type="number" min="0.25" max="168" step="0.25" value={form.estimated_hours} onChange={(event) => setForm({ ...form, estimated_hours: event.target.value })} className={inputCls} /></Field><div className="flex justify-end gap-2"><button type="button" onClick={() => setSelected(null)} className="rounded px-4 py-2 text-sm text-slate-600 hover:bg-slate-100">Cancel</button><PrimaryButton disabled={saving}>Save schedule</PrimaryButton></div></form>}
      </Modal>
    </Page>
  );
}
